import { useState } from "react";
import { useSim } from "../state/store";
import type { EvidenceItem } from "../state/store";

/**
 * Corner "Attack Exposure Map" HUD — replaces the single Risk Meter
 * percentage with four honest buckets of what an attacker actually got:
 * Identity, Information, Money, Operations. Same corner slot as RiskMeter
 * (top-left); a course sets `exposureMap: true` + `riskMeter: false` so only
 * one of the two ever renders. Expandable into the same evidence log the
 * Debrief shows, so a learner can review exactly what happened so far.
 */
export function ExposureMap() {
  const map = useSim((s) => s.exposureMap());
  const evidence = useSim((s) => s.evidence());
  const chapters = useSim((s) => s.chapters());
  const enterReview = useSim((s) => s.enterReview);
  const reviewing = useSim((s) => !!s.reviewFromBeatId);
  const [expanded, setExpanded] = useState(false);
  // Independent of the evidence-log disclosure above, and ALWAYS starts
  // collapsed per Change 10 — most learners never need it, so it shouldn't
  // compete for attention with the exposure bars on every screen.
  const [chaptersOpen, setChaptersOpen] = useState(false);

  if (!map.enabled) return null;

  const overall = Math.max(0, ...map.dims.map((d) => d.percent));
  const band = overall <= 20 ? "controlled" : overall <= 45 ? "risky" : overall <= 70 ? "serious" : "incident";

  return (
    <div
      className={`exposure-map exposure-map--${band} ${expanded ? "exposure-map--open" : ""}`}
      role="status"
      aria-live="polite"
    >
      <button
        type="button"
        className="exposure-map-head"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-label={expanded ? "Collapse attack exposure map" : "Expand attack exposure map"}
      >
        <span className="exposure-map-kicker">Attack exposure</span>
        <span className="exposure-map-chevron" aria-hidden>
          {expanded ? "\u25B4" : "\u25BE"}
        </span>
      </button>

      <div className="exposure-map-dims">
        {map.dims.map((d) => (
          <div className="exposure-map-dim" key={d.dimension}>
            <span className="exposure-map-dim-label">{d.label}</span>
            <div className="exposure-map-dim-track" aria-hidden>
              <div
                className={`exposure-map-dim-fill exposure-map-dim-fill--${d.dimension}`}
                style={{ width: `${d.percent}%` }}
              />
            </div>
          </div>
        ))}
      </div>

      {expanded && (
        <div className="exposure-map-card">
          <div className="exposure-map-card-head">
            Evidence collected <span className="exposure-map-card-count">{evidence.length}</span>
          </div>
          <EvidenceList items={evidence} />
        </div>
      )}

      {chapters.length > 0 && (
        <div className="exposure-map-chapters">
          <button
            type="button"
            className="exposure-map-chapters-head"
            onClick={() => setChaptersOpen((v) => !v)}
            aria-expanded={chaptersOpen}
            aria-label={chaptersOpen ? "Collapse course chapters" : "Expand course chapters"}
          >
            <span>Course chapters</span>
            <span className="exposure-map-chevron" aria-hidden>
              {chaptersOpen ? "\u25B4" : "\u25BE"}
            </span>
          </button>
          {chaptersOpen && (
            <ul className="exposure-map-chapter-list">
              {chapters.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    className={`exposure-map-chapter ${c.reached ? "is-reached" : "is-locked"} ${
                      c.current ? "is-current" : ""
                    }`}
                    disabled={!c.reached || reviewing}
                    onClick={() => enterReview(c.beatId)}
                    title={c.reached ? `Review ${c.title}` : "Not reached yet"}
                  >
                    <span className="exposure-map-chapter-dot" aria-hidden />
                    <span className="exposure-map-chapter-title">{c.title}</span>
                    {c.reached && <span className="exposure-map-chapter-go" aria-hidden>Review →</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function EvidenceList({ items }: { items: EvidenceItem[] }) {
  if (items.length === 0) {
    return <p className="exposure-map-empty">Nothing recorded yet — keep going.</p>;
  }
  return (
    <ul className="exposure-map-items">
      {items
        .slice()
        .reverse()
        .map((it) => (
          <li key={`${it.id}-${it.at}`} className={`exposure-map-item ${it.kind}`}>
            <span className="exposure-map-item-ico" aria-hidden>
              {it.kind === "risk" ? "\u26A0" : "\u2713"}
            </span>
            <span className="exposure-map-item-label">{it.label}</span>
          </li>
        ))}
    </ul>
  );
}
